import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  arrayUnion,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase'; // Correctly import named export 'db'
import type {
  Dashboard,
  CreateDashboardData,
  UpdateDashboardData,
  DashboardChartItem,
  DashboardLayoutConfiguration
} from '@shared/types/dashboard.types'; // Path alias for shared types

const getDashboardsCollectionRef = (organizationId: string, userId: string) => {
  return collection(db, `organizations/${organizationId}/users/${userId}/dashboards`); // Use imported 'db'
};

const getDashboardDocRef = (organizationId: string, userId: string, dashboardId: string) => {
  return doc(db, `organizations/${organizationId}/users/${userId}/dashboards/${dashboardId}`); // Use imported 'db'
};

export const DashboardService = {
  async createDashboard(
    organizationId: string,
    userId: string,
    dashboardData: CreateDashboardData
  ): Promise<Dashboard> {
    const dashboardsColRef = getDashboardsCollectionRef(organizationId, userId);
    const dataToSave: any = { // Use any for flexibility before saving
      ...dashboardData,
      userId: dashboardData.userId || userId,
      organizationId: dashboardData.organizationId || organizationId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    if (dataToSave.description === undefined) {
      delete dataToSave.description;
    }

    console.log('[DashboardService.create] Path:', dashboardsColRef.path);
    console.log('[DashboardService.create] Data to save (raw):', {...dataToSave});

    const docRef = await addDoc(dashboardsColRef, dataToSave);
    const docSnap = await getDoc(docRef);
    
    // Construct the Dashboard object for return, ensuring types match
    const savedData = docSnap.data();
    return {
      id: docRef.id,
      title: savedData?.title,
      description: savedData?.description,
      organizationId: savedData?.organizationId,
      userId: savedData?.userId,
      layoutConfiguration: savedData?.layoutConfiguration,
      charts: savedData?.charts,
      createdAt: savedData?.createdAt as Timestamp, // Expect Timestamp from Firestore
      updatedAt: savedData?.updatedAt as Timestamp, // Expect Timestamp from Firestore
    } as Dashboard; // Cast to Dashboard ensuring all required fields are present
  },

  async getUserDashboards(organizationId: string, userId: string): Promise<Dashboard[]> {
    const dashboardsColRef = getDashboardsCollectionRef(organizationId, userId);
    const q = query(dashboardsColRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Dashboard));
  },

  async getDashboard(
    organizationId: string,
    userId: string,
    dashboardId: string
  ): Promise<Dashboard | null> {
    const docRef = getDashboardDocRef(organizationId, userId, dashboardId);
    const snapshot = await getDoc(docRef);
    if (snapshot.exists()) {
      return { id: snapshot.id, ...snapshot.data() } as Dashboard;
    }
    return null;
  },

  async updateDashboard(
    organizationId: string,
    userId: string,
    dashboardId: string,
    updates: UpdateDashboardData
  ): Promise<void> {
    const docRef = getDashboardDocRef(organizationId, userId, dashboardId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  },

  async deleteDashboard(
    organizationId: string,
    userId: string,
    dashboardId: string
  ): Promise<void> {
    const docRef = getDashboardDocRef(organizationId, userId, dashboardId);
    await deleteDoc(docRef);
  },

  async addChartToDashboard(
    organizationId: string,
    userId: string,
    dashboardId: string,
    chartItem: DashboardChartItem
  ): Promise<void> {
    const docRef = getDashboardDocRef(organizationId, userId, dashboardId);
    await updateDoc(docRef, {
      charts: arrayUnion(chartItem),
      updatedAt: serverTimestamp(),
    });
  },

  async removeChartFromDashboard(
    organizationId: string,
    userId: string,
    dashboardId: string,
    chartId: string 
  ): Promise<void> {
    const docRef = getDashboardDocRef(organizationId, userId, dashboardId);
    const dashboard = await this.getDashboard(organizationId, userId, dashboardId);
    if (dashboard) {
      const updatedCharts = dashboard.charts.filter(c => c.chartId !== chartId);
      await updateDoc(docRef, {
        charts: updatedCharts,
        updatedAt: serverTimestamp(),
      });
    }
  },

  async updateDashboardCharts(
    organizationId: string,
    userId: string,
    dashboardId: string,
    charts: DashboardChartItem[] 
  ): Promise<void> {
    const docRef = getDashboardDocRef(organizationId, userId, dashboardId);
    await updateDoc(docRef, {
      charts: charts,
      updatedAt: serverTimestamp(),
    });
  },
  
  async updateDashboardLayout(
    organizationId: string,
    userId: string,
    dashboardId: string,
    layoutConfiguration: Partial<DashboardLayoutConfiguration>
  ): Promise<void> {
    const docRef = getDashboardDocRef(organizationId, userId, dashboardId);
    await updateDoc(docRef, {
      layoutConfiguration: layoutConfiguration,
      updatedAt: serverTimestamp(),
    });
  },
}; 